import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  adicionarTagFoto,
  buscarTagsFoto,
  buscarTagsMultiplasFotos,
  buscarTagsObra,
  removerTagFoto,
  TagCatalogo} from '../services/fotoTagsService';

interface UseFotoTagsOptions {
  obraId: string;
  fotoId?: string;
  autoCarregar?: boolean;
}

export function useFotoTags({ obraId, fotoId, autoCarregar = true }: UseFotoTagsOptions) {
  const [tagsDisponiveis, setTagsDisponiveis] = useState<TagCatalogo[]>([]);
  const [tagsFoto, setTagsFoto] = useState<TagCatalogo[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAcao, setLoadingAcao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');

  // Carregar tags disponíveis da obra
  const carregarTagsObra = useCallback(async (termoBusca?: string) => {
    if (!obraId) return;

    try {
      setLoading(true);
      setErro(null);
      const tags = await buscarTagsObra(obraId, termoBusca, 30);
      setTagsDisponiveis(tags);
    } catch (err) {
      setErro((err as Error).message);
      console.error('Erro ao carregar tags da obra:', err);
    } finally {
      setLoading(false);
    }
  }, [obraId]);

  // Carregar tags da foto específica
  const carregarTagsFoto = useCallback(async () => {
    if (!fotoId) return;

    try {
      setLoading(true);
      setErro(null);
      const tags = await buscarTagsFoto(fotoId);
      setTagsFoto(tags);
    } catch (err) {
      setErro((err as Error).message);
      console.error('Erro ao carregar tags da foto:', err);
    } finally {
      setLoading(false);
    }
  }, [fotoId]);

  // Adicionar tag
  const adicionar = useCallback(async (tagNome: string, usuarioId?: string) => {
    if (!fotoId || !obraId || !tagNome.trim()) return false;

    try {
      setLoadingAcao(true);
      setErro(null);
      await adicionarTagFoto(fotoId, tagNome.trim(), obraId, usuarioId);

      // Recarrega as tags da foto e da obra
      await Promise.all([
        carregarTagsFoto(),
        carregarTagsObra(busca)
      ]);

      return true;
    } catch (err) {
      setErro((err as Error).message);
      console.error('Erro ao adicionar tag:', err);
      return false;
    } finally {
      setLoadingAcao(false);
    }
  }, [fotoId, obraId, busca, carregarTagsFoto, carregarTagsObra]);

  // Remover tag
  const remover = useCallback(async (tagId: string) => {
    if (!fotoId) return false;

    try {
      setLoadingAcao(true);
      setErro(null);
      await removerTagFoto(fotoId, tagId);

      // Recarrega as tags da foto
      await carregarTagsFoto();

      return true;
    } catch (err) {
      setErro((err as Error).message);
      console.error('Erro ao remover tag:', err);
      return false;
    } finally {
      setLoadingAcao(false);
    }
  }, [fotoId, carregarTagsFoto]);

  // Toggle tag (adiciona se não tem, remove se tem)
  const toggle = useCallback(async (tag: TagCatalogo, usuarioId?: string) => {
    const jaTemTag = tagsFoto.some(t => t.id === tag.id);

    if (jaTemTag) {
      return remover(tag.id);
    } else {
      return adicionar(tag.nome, usuarioId);
    }
  }, [tagsFoto, adicionar, remover]);

  // Buscar tags com debounce
  const pesquisar = useCallback(async (termo: string) => {
    setBusca(termo);
    await carregarTagsObra(termo || undefined);
  }, [carregarTagsObra]);

  // Verificar se foto já tem uma tag
  const temTag = useCallback((tagId: string) => {
    return tagsFoto.some(t => t.id === tagId);
  }, [tagsFoto]);

  // Carregar dados iniciais
  useEffect(() => {
    if (autoCarregar && obraId) {
      carregarTagsObra();
    }
  }, [autoCarregar, obraId, carregarTagsObra]);

  useEffect(() => {
    if (autoCarregar && fotoId) {
      carregarTagsFoto();
    }
  }, [autoCarregar, fotoId, carregarTagsFoto]);

  return {
    // Estado
    tagsDisponiveis,
    tagsFoto,
    loading,
    loadingAcao,
    erro,
    busca,

    // Ações
    adicionar,
    remover,
    toggle,
    pesquisar,
    temTag,

    // Recarregar
    recarregarTagsObra: carregarTagsObra,
    recarregarTagsFoto: carregarTagsFoto
  };
}

// Hook para carregar tags de múltiplas fotos (batch)
// IMPORTANTE: Este hook deve ser usado com um array memoizado de IDs
export function useFotosComTags(fotoIds: string[]) {
  const [tagsMap, setTagsMap] = useState<Map<string, TagCatalogo[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const lastIdsRef = useRef<string>('');

  // Serializa os IDs para comparação estável
  const idsKey = useMemo(() => fotoIds.slice(0, 50).sort().join(','), [fotoIds]);

  const carregar = useCallback(async (forceReload = false) => {
    // Evita recarregar se os IDs são os mesmos
    if (!forceReload && idsKey === lastIdsRef.current) {
      return;
    }

    if (fotoIds.length === 0) {
      setTagsMap(new Map());
      lastIdsRef.current = '';
      return;
    }

    // Limita a 50 fotos por vez para evitar URLs muito longas
    const idsParaBuscar = fotoIds.slice(0, 50);

    try {
      setLoading(true);
      const result = await buscarTagsMultiplasFotos(idsParaBuscar);
      setTagsMap(result);
      lastIdsRef.current = idsKey;
    } catch (err) {
      console.error('Erro ao carregar tags das fotos:', err);
    } finally {
      setLoading(false);
    }
  }, [fotoIds, idsKey]);

  // Carrega quando os IDs mudam
  useEffect(() => {
    if (idsKey !== lastIdsRef.current) {
      carregar();
    }
  }, [idsKey, carregar]);

  const getTagsFoto = useCallback((fotoId: string): TagCatalogo[] => {
    return tagsMap.get(fotoId) || [];
  }, [tagsMap]);

  return {
    tagsMap,
    loading,
    getTagsFoto,
    recarregar: () => carregar(true)
  };
}
